import Lenis from 'lenis';
import { getElement, getElements } from '../helpers/functions';
import { baseElements } from '../helpers/constants';
const {
  $header,
  $navigationStiky
} = baseElements;

/**
 * Singleton instance of Lenis scroll
 * Ensures only one scroll instance exists throughout the application
 * @type {Lenis|null}
 */
let scrollInstance = null;

/**
 * Initializes the smooth scroll functionality
 * @param {Object} options - Configuration options for Lenis
 * @param {boolean} [options.autoRaf=true] - Whether to auto-handle requestAnimationFrame
 * @param {boolean} [options.smoothWheel=true] - Enable smooth scrolling for mouse wheel
 * @returns {Lenis} The Lenis scroll instance
 */
export const initScroll = (options = {}) => {
  const scrollToHash = hash => {
    if (!hash) return;
    const target = getElement(hash);
    if (target) {
      // eslint-disable-next-line max-len
      const offsetValue = ($navigationStiky ? -$header.offsetHeight - $navigationStiky.offsetHeight : -$header.offsetHeight) - 20;
      scrollInstance.scrollTo(target, {
        offset: offsetValue
      });
    }
  };
  const handleHashChange = e => {
    e.preventDefault();
    const newHash = window.location.hash;
    if (newHash) {
      scrollToHash(newHash);
    } else {
      scrollInstance.scrollTo(0);
    }
  };
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  };
  if (!scrollInstance) {
    // Initialize Lenis with default and custom options
    scrollInstance = new Lenis({
      autoRaf: true,
      smoothWheel: true,
      ...options
    });
    const resizeObserver = new ResizeObserver(debounce(() => scrollInstance.resize(), 250));
    resizeObserver.observe(document.body);

    // Handle all anchor links on the page
    getElements('a[href^="#"]:not([href="#"])').forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault();
        const targetId = link.getAttribute('href');
        if (targetId !== '#') {
          window.history.pushState(null, '', targetId);
        }
        scrollToHash(targetId);
      });
    });
    if (window.location.hash) {
      window.scrollTo(0, 0);
      setTimeout(() => {
        scrollToHash(window.location.hash);
      }, 100);
    }
  }
  window.addEventListener('hashchange', handleHashChange.bind(this));
  return scrollInstance;
};

/**
 * Gets the current Lenis scroll instance
 * Useful for accessing scroll functionality from other components
 * @returns {Lenis|null} The current Lenis instance or null if not initialized
 */
export const getScroll = () => scrollInstance;